import {BehaviorSubject, combineLatest, of, ReplaySubject, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {mc} from '@/core';
import {userApi} from '@/api/user';
import {userInfo$} from './user';
import {wwData$} from './worker-data';
import {accountGray$, initAccountGray$} from './account-gray';
import type {UtaState} from '@px/ws-data';

type UtaAccountMode = 'Single' | 'Multi';
type SpotUnifiedAccountUpgradeStatus = 0 | 1 | 2 | 3; // 0: 未升级; 1: 答题完成; 2: 升级中; 3: 升级完成

/**
 * 0: 'INIT', // 未开始升级
 * 1: 'BAN_SPOT_DONE', // 现货账号禁用完成
 * 2: 'BAN_MARGIN_DONE', // marign账号封禁完成
 * 3: 'TRANSFER_SPOT_ASSET_ING', // 现货资产迁移中
 * 4: 'TRANSFER_SPOT_ASSET_DONE', // 现货资产迁移完成
 * 5: 'TRANSFER_MARGIN_ASSET_ING', // margin资产迁移中
 * 6: 'TRANSFER_MARGIN_ASSET_DONE', // margin资产迁移完成
 * 7: 'SET_FEE_DONE', // 设置费率完成
 * 8: 'SET_DEPOSIT_ACCOUNT_DONE', // 设置默认入金账户完成
 */
type spotUnifiedAccountUpgradeStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export interface UnifiedAccountUpgradeInfo {
  userId: number;
  spotUnifiedAccountUpgradeStatus: SpotUnifiedAccountUpgradeStatus;
  spotUnifiedAccountUpgradeStep: spotUnifiedAccountUpgradeStep;
  withdrawAndDepositCheckPassed: boolean; // 法币和数字币不能有在途的提现和充值
  updateTime: number;
  withdrawAndDepositCheckDetail: {
    bankTransferCheckPassed: boolean; // 银行卡
    thirdPartyCheckPassed: boolean; // 第三方
    creditOrDebitCardCheckPassed: boolean; // 信用卡
    p2pOrderCheckPassed: boolean; // P2P order
    p2pAdsCheckPassed: boolean; // P2P ad
    cryptoDepositCheckPassed: boolean; // 数字币充值
    cryptoWithdrawCheckPassed: boolean; // 数字币提现
  } | null;
  spotOrderCheckPassed: boolean; //现货和margin挂单
  spotOrderCheckDetail: {
    spotOrderCheckPassed: boolean; // 现货订单
    marginOrderCheckPassed: boolean; // margin 订单
  } | null;
  marginBorrowCheckPassed: boolean; // margin 借款
  marginBorrowCheckDetail: {
    marginBorrowCheckPassed: boolean; // margin 借款
    marginInterestCheckPassed: boolean; // margin 利息
  } | null;
}

export const utaState$ = new BehaviorSubject<UtaState>(createInitUatState());
export const isUtaSpot$ = new BehaviorSubject<boolean>(false);
export const isUtaSpotUpgrading$ = new BehaviorSubject<boolean>(false);
export const utaAccountMode$ = new BehaviorSubject<UtaAccountMode>('Multi');

const loadUtaInfo$ = new Subject<void>();
export const unifiedAccountUpgradeInfo$ = new BehaviorSubject<UnifiedAccountUpgradeInfo>(createUnifiedAccountUpgradeInfo());
export const isSupportUtaSpot$ = new ReplaySubject<boolean>(1);
export const unifiedAccountUpgradedTime$ = new BehaviorSubject(0);

utaState$
  .pipe(
    map((o) => o.spot),
    distinctUntilChanged()
  )
  .subscribe((spotState) => {
    isUtaSpotUpgrading$.next(checkIsUtaUpgrading(spotState));
    isUtaSpot$.next(checkIsUtaSpot(spotState));
  });

combineLatest([accountGray$, initAccountGray$])
  .pipe(
    filter(([, initAccountGray]) => !initAccountGray),
    map(([accountGray]) => {
      return accountGray ? !!accountGray.spotUta : false;
    })
  )
  .subscribe((state) => {
    isSupportUtaSpot$.next(state);
  });

unifiedAccountUpgradeInfo$
  .pipe(
    map((o) => o.updateTime),
    distinctUntilChanged()
  )
  .subscribe(unifiedAccountUpgradedTime$);

isSupportUtaSpot$.pipe(distinctUntilChanged(), filter(Boolean)).subscribe(() => {
  loadUtaInfo$.next();
});

loadUtaInfo$.pipe(switchMap(loadUtaInfo)).subscribe((o) => unifiedAccountUpgradeInfo$.next(o));

userInfo$
  .pipe(
    map((o) => o.uta),
    filter(Boolean)
  )
  .subscribe(utaState$);

async function loadUtaInfo() {
  const {data, error} = await userApi.getUtaInfo();
  if (error) {
    mc.send('toast', 'error', error);
  }
  return data || createUnifiedAccountUpgradeInfo();
}

wwData$
  .pipe(
    filter((o) => o.type === 'uta-state'),
    map((o) => o.data as UtaState)
  )
  .subscribe(utaState$);

export function checkIsUtaSpot(spotState: 0 | 1 | 2) {
  return spotState === 2;
}

export function checkIsUtaUpgrading(spotState: 0 | 1 | 2) {
  return spotState === 1;
}

function createUnifiedAccountUpgradeInfo(): UnifiedAccountUpgradeInfo {
  return {
    userId: 0,
    spotUnifiedAccountUpgradeStatus: 0,
    spotUnifiedAccountUpgradeStep: 0,
    updateTime: 0,
    withdrawAndDepositCheckPassed: false,
    withdrawAndDepositCheckDetail: null,
    spotOrderCheckPassed: false,
    spotOrderCheckDetail: null,
    marginBorrowCheckPassed: false,
    marginBorrowCheckDetail: null,
  };
}

function createInitUatState(): UtaState {
  return {
    spot: 0,
  };
}
